import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useZendeskIntegrationUpdateMutation from "@babackapp/private-api-client/client/use-update-zendesk-integration";
import { Button } from "@babackapp/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import useTranslation from "@babackapp/translations/use-translation";
import { z } from "zod";
import IntegrationPreview from "./integration-preview";

const formSchema = z.object({
  activated: z.boolean(),
  email: z.string().email(),
  apiKey: z.string(),
  subDomain: z.string(),
});

const ZendeskIntegration = () => {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { activated, email, apiKey, subDomain } = shop.integrations.zendesk;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { activated, email, apiKey, subDomain },
  });

  const { mutateAsync: updateZendeskIntegration } =
    useZendeskIntegrationUpdateMutation(shop);

  const onSubmit = form.handleSubmit((data) => {
    updateZendeskIntegration(data).then(() => form.reset(data));
  });

  return (
    <Form {...form}>
      <form
        onSubmit={onSubmit}
        className="flex flex-col justify-start gap-8 mt-[16px]"
      >
        <div className="flex gap-4 w-full">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>
                  {t("settings.integrations.thirdpart.zendesk.email")}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="subDomain"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>
                  {t("settings.integrations.thirdpart.zendesk.subDomain")}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="apiKey"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.thirdpart.zendesk.apiKey")}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <Button type="submit" disabled={!form.formState.isDirty}>
          {t("misc.save")}
        </Button>
      </form>
    </Form>
  );
};

export function ZendeskIntegrationPreview() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { mutateAsync: updateActivation } =
    useZendeskIntegrationUpdateMutation(shop);

  const onActivationUpdate = useCallback(
    (activated: boolean) => {
      updateActivation({ activated });
    },
    [shop, updateActivation],
  );

  if (!shop || !shop.plan.zendesk) return null;

  return (
    <IntegrationPreview
      title="Zendesk"
      logo="/integrations/zendesk.png"
      description={t("settings.integrations.thirdpart.zendesk.description")}
      onActivationUpdate={onActivationUpdate}
      activated={shop.integrations.zendesk.activated}
      seeMoreUrl="https://zendesk.com"
    >
      <ZendeskIntegration />
    </IntegrationPreview>
  );
}
